import { z } from "zod";
import type { FiledObligation } from "./compliance-regimes";
import type { RegistrationNumberScheme } from "./enums";
import { withLocalTimeOnStayDate } from "./local-time";
import type {
  RegistrationObligation,
  RegistrationReportScope,
} from "./registration";
import type { RegistrationFieldRequirement } from "./registration-fields";
import {
  STATISTICS_REGIONS,
  type StatisticsRegion,
  statisticsRegionValues,
} from "./statistics-filing";

// Government guest-registration systems (bucket-A national APIs)
//
// Catalog of the national registration systems a host files guests to, plus
// the per-system credential SHAPE the host must provide. This is the routing
// and onboarding knowledge layer for the credential vault: it drives
// both the host-facing onboarding form (what to enter) and the server-side
// split between secret fields (encrypted into the `enc:` ref) and non-secret
// config (stored in plaintext).
//
// One POLICE system per country, but a country may add a statistics system
// alongside it (Italy: `alloggiati_web` + `istat`) — see `category`.
//
// IMPORTANT: nothing here is a secret value. These are field DEFINITIONS. The
// actual credentials a host enters are partitioned by `secret` and the secret
// ones are encrypted server-side; they never live in this catalog, in the
// client bundle, or in a log line.

/**
 * The national systems we hold per-host credentials for. Most are bucket-A
 * per-guest police/registration APIs; `istat` is the second credential slot for
 * Italy — regional tourism-statistics reporting, a distinct
 * obligation from the Alloggiati police feed. The `category` on each spec keeps
 * the two kinds apart for routing and the settings surface.
 */
export const registrationSystemValues = [
  "ses_hospedajes", // Spain — police (bucket A)
  "alloggiati_web", // Italy — police (bucket A)
  "siba", // Portugal — police (bucket A)
  "evisitor", // Croatia — police (bucket A)
  "eturizem", // Slovenia AJPES — police (bucket A)
  "istat", // Italy — regional tourism statistics
] as const;

export const RegistrationSystemSchema = z.enum(registrationSystemValues);
export type RegistrationSystem = (typeof registrationSystemValues)[number];

/**
 * How a credential field is entered. `secret` (below) is orthogonal: a
 * `username` is entered as plain `text` yet is still stored encrypted. The
 * type only picks the input widget; `secret` decides where the value lands.
 */
export type RegistrationFieldType = "text" | "password" | "file" | "integer";

export interface RegistrationFieldDescriptor {
  /** Accepted file types for `type: "file"`, e.g. `.p12,.pfx`. */
  accept?: string;
  /**
   * The register that lists the facility this number names: a save refuses a
   * number it does not list in the property's municipality.
   */
  facilityRegister?: RegistrationNumberScheme;
  /** Stable key used on the wire and as the secret/config map key. */
  key: string;
  label: string;
  required: boolean;
  /** Secret values are encrypted into the `enc:` ref and never returned. */
  secret: boolean;
  type: RegistrationFieldType;
}

/**
 * What kind of obligation a system serves. A country can have one of each:
 * Italy files guests to `alloggiati_web` (police) AND aggregates to `istat`
 * (statistics). Guest routing (`registrationSystemForCountry`) only ever
 * resolves the `guest_registration` system; the settings surface shows both.
 */
export const registrationSystemCategoryValues = [
  "guest_registration",
  "statistics",
] as const;
export type RegistrationSystemCategory =
  (typeof registrationSystemCategoryValues)[number];

/**
 * A shorter statutory window for the arrival report of a short stay: when the
 * departure is within `maxStayHours` of the arrival, the report is due within
 * `deadlineHours` of arrival instead of the general deadline.
 */
export interface ShortStayDeadline {
  deadlineHours: number;
  maxStayHours: number;
}

/** The legal basis and recipient of a guest registration, as a guest reads it. */
export interface GuestRegistrationNotice {
  /** The register the guest is reported to, e.g. "Polizia di Stato (Alloggiati Web)". */
  recipient: string;
  /** The statute that requires the report, e.g. "art. 109 TULPS". */
  statute: string;
}

/**
 * A system's notice, plus the regions whose guests go to another register
 * under the same statute (Catalonia's and the Basque Country's own police
 * rather than the Spanish Interior Ministry). `regions` lists the names a
 * property's stored region can take for that area: it is Google's long name in
 * whichever language the host's app asked in.
 */
export interface GuestRegistrationNoticeSpec extends GuestRegistrationNotice {
  regionalRecipients?: readonly {
    recipient: string;
    regions: readonly string[];
  }[];
}

/** One statutory fact, with the provision that states it and where it was read. */
export interface CitedFact<T> {
  legalBasis: string;
  sourceUrl: string;
  value: T;
}

/** What the statute asks of every filing to a system Vidimo files to. */
export interface GuestFilingRules {
  /** Hours after arrival the arrival report is due, before any short-stay window. */
  arrivalWithinHours: CitedFact<number>;
  /** Hours after departure the departure report is due, or null where departures are not reported. */
  departureWithinHours: CitedFact<number | null>;
  whoMustReport: CitedFact<RegistrationReportScope>;
}

export interface RegistrationSystemSpec {
  /** What its filings discharge beyond its category's own obligation. */
  alsoFiles: readonly FiledObligation[];
  /** Which obligation this system serves (per-guest police vs statistics). */
  category: RegistrationSystemCategory;
  country: string;
  /** ISO-3166-1 alpha-2 country this system serves (matches `properties.countryCode`). */
  countryCode: string;
  /** Host-facing name of the system. */
  displayName: string;
  /**
   * The authority's portal: where the host enrolls to obtain the credentials,
   * and checks or files a guest by hand. Its home page: these portals reach a
   * filing only by a search after sign-in, never by an address of its own.
   */
  enrollmentUrl?: string;
  fields: RegistrationFieldDescriptor[];
  /**
   * The statute's filing rules, which the filing engine builds every filing
   * from. Null for a system with no connector, which files nothing.
   */
  filing: GuestFilingRules | null;
  /**
   * What a guest is told at the moment they hand over their document: the
   * statute that obliges the property to record them, and the register it
   * reports them to. Proper nouns in the authority's own language, so every
   * portal locale quotes them as they are. Null for a statistics system, which
   * guest routing never resolves, and for a system that reports only some
   * guests (SIBA), where naming it would be false for the rest.
   */
  guestNotice: GuestRegistrationNoticeSpec | null;
  /**
   * The guest fields this system's wire cannot be filed without: the legal
   * floor. Its connector rejects on this list and no other, and every capture
   * surface asks for it, so what is collected and what is refused are the same
   * list. Empty for a system with no connector yet.
   */
  requiredGuestFields: readonly RegistrationFieldRequirement[];
  /**
   * The field holding a key the authority issues beside the password and
   * refuses on its own, so a probe's `service_key` refusal names it. Null
   * where the password is the whole sign-in.
   */
  serviceKeyField: string | null;
  /**
   * The statute's shorter arrival deadline for a short stay, or null where one
   * window applies to every stay.
   */
  shortStayDeadline: ShortStayDeadline | null;
  /**
   * Whether this authority lets a host take an accepted report back out of its
   * register, and the product can therefore offer it.
   *
   * A fact about the register, not about our code: AJPES models a cancelled
   * record (`storno`), while Alloggiati Web and Ross 1000 expose no equivalent
   * on their wire. Stated here so the connector's optional `withdraw` and the
   * host surface read ONE fact — the alternative was a button that promises a
   * named office will cancel a record and then answers with a 409.
   */
  supportsWithdraw: boolean;
  system: RegistrationSystem;
  /**
   * The country's easternmost mainland zone. Every filing deadline is also
   * worked out in this zone, and the soonest deadline wins: a property saved
   * with no zone, an unreadable one, or a real zone that disagrees with its
   * country ("UTC" or London for a Rome flat, Istanbul across a clock change)
   * would otherwise push the deadline past the law's.
   */
  timeZone: string;
}

const ITALY_POLICE_REPORTING_URL =
  "https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:regio.decreto:1931-06-18;773:1~art109";
const SLOVENIA_RESIDENCE_REGISTRATION_URL =
  "https://pisrs.si/pregledPredpisa?id=ZAKO6046";

/**
 * Where a property's eTurizem guest-book numbers start. AJPES lets the reporter
 * number as it likes ("možen je popravek oz. drugačno številčenje s strani
 * poročevalca", Navodilo eTurizem, dnevno poročanje), and a record is keyed by
 * `(idNO, year, zst)`, so numbering far above any other program's book lets
 * that program keep filing the facility beside Vidimo without either
 * overwriting the other's guests. AJPES's test server accepts every report
 * under 7-digit numbers (live-smoke.test.ts).
 */
export const ETURIZEM_GUESTBOOK_BASE = 1_000_001;

export const REGISTRATION_SYSTEMS: Record<
  RegistrationSystem,
  RegistrationSystemSpec
> = {
  alloggiati_web: {
    alsoFiles: [],
    category: "guest_registration",
    country: "Italy",
    countryCode: "IT",
    displayName: "Alloggiati Web",
    enrollmentUrl: "https://alloggiatiweb.poliziadistato.it",
    fields: [
      {
        key: "username",
        label: "Username",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "password",
        label: "Password",
        required: true,
        secret: true,
        type: "password",
      },
      {
        key: "wskey",
        label: "Web-service key (WS Key)",
        required: true,
        secret: true,
        type: "password",
      },
      {
        key: "idAppartamento",
        label: "Apartment ID",
        required: false,
        secret: false,
        type: "integer",
      },
    ],
    filing: {
      arrivalWithinHours: {
        legalBasis: "art. 109 co. 3 TULPS",
        sourceUrl: ITALY_POLICE_REPORTING_URL,
        value: 24,
      },
      // The statute reports arrivals only.
      departureWithinHours: {
        legalBasis: "art. 109 co. 3 TULPS",
        sourceUrl: ITALY_POLICE_REPORTING_URL,
        value: null,
      },
      whoMustReport: {
        legalBasis: "art. 109 co. 1 TULPS",
        sourceUrl: ITALY_POLICE_REPORTING_URL,
        value: "all_guests",
      },
    },
    guestNotice: {
      recipient: "Polizia di Stato (Alloggiati Web)",
      statute: "art. 109 TULPS",
    },
    // The schedina's mandatory columns (TABELLA 1). The document triple is
    // only checked for a lone guest or a party's head (types 16/17/18).
    requiredGuestFields: [
      { field: "firstName" },
      { field: "lastName" },
      { field: "dateOfBirth" },
      { field: "sex" },
      { field: "nationality" },
      // The police document table has no generic code.
      { field: "documentType", headOnly: true, oneOf: ["passport", "id_card"] },
      { field: "documentNumber", headOnly: true },
      { field: "issuingCountry", headOnly: true },
      { field: "birthCountry" },
      // Resolved against the police's municipality table, which only exists
      // for someone born in Italy.
      {
        field: "birthPlace",
        onlyWhen: { equals: "IT", field: "birthCountry" },
      },
      { field: "arrivalDate" },
      { field: "departureDate" },
    ],
    // Art. 109 co. 3 TULPS, as amended by art. 5 D.L. 53/2019: a stay of 24
    // hours or less is reported within 6 hours of arrival.
    serviceKeyField: "wskey",
    shortStayDeadline: { deadlineHours: 6, maxStayHours: 24 },
    // Their wire has no cancellation: a correction is a fresh send, and a
    // record entered in error is settled with the Questura directly.
    supportsWithdraw: false,
    system: "alloggiati_web",
    timeZone: "Europe/Rome",
  },
  eturizem: {
    alsoFiles: ["statistics", "tourist-tax"],
    category: "guest_registration",
    country: "Slovenia",
    countryCode: "SI",
    displayName: "eTurizem (AJPES)",
    enrollmentUrl: "https://www.ajpes.si",
    fields: [
      {
        key: "username",
        label: "AJPES portal username",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "password",
        label: "AJPES portal password",
        required: true,
        secret: true,
        type: "password",
      },
      {
        accept: ".p12,.pfx",
        key: "certificate",
        label: "Digital certificate (.p12 / KDP)",
        required: true,
        secret: true,
        type: "file",
      },
      {
        key: "certificatePassword",
        label: "Certificate passphrase",
        required: false,
        secret: true,
        type: "password",
      },
      {
        facilityRegister: "si_rno",
        key: "rno",
        label: "RNO number",
        required: true,
        secret: false,
        type: "integer",
      },
    ],
    filing: {
      arrivalWithinHours: {
        legalBasis: "ZPPreb-1, art. 40(3)",
        sourceUrl: SLOVENIA_RESIDENCE_REGISTRATION_URL,
        value: 12,
      },
      departureWithinHours: {
        legalBasis: "ZPPreb-1, art. 40(3)",
        sourceUrl: SLOVENIA_RESIDENCE_REGISTRATION_URL,
        value: 12,
      },
      whoMustReport: {
        legalBasis: "ZPPreb-1, art. 39(2)",
        sourceUrl: SLOVENIA_RESIDENCE_REGISTRATION_URL,
        value: "all_guests",
      },
    },
    guestNotice: {
      recipient: "AJPES (eTurizem)",
      statute: "ZPPreb-1",
    },
    // Every `knjigaGostov` row carries these (schema V1.1, confirmed against
    // the apiTest.zip samples).
    requiredGuestFields: [
      { field: "firstName" },
      { field: "lastName" },
      { field: "dateOfBirth" },
      { field: "sex" },
      { field: "nationality" },
      { field: "documentType" },
      { field: "documentNumber" },
      { field: "arrivalDate" },
      { field: "departureDate" },
    ],
    serviceKeyField: null,
    shortStayDeadline: null,
    // A storno: the record stays and its validity flips to cancelled. Their own
    // rule is a guest entered for a facility where they are not staying.
    supportsWithdraw: true,
    system: "eturizem",
    timeZone: "Europe/Ljubljana",
  },
  evisitor: {
    alsoFiles: ["tourist-tax"],
    category: "guest_registration",
    country: "Croatia",
    countryCode: "HR",
    displayName: "eVisitor",
    enrollmentUrl: "https://www.evisitor.hr",
    fields: [
      {
        key: "username",
        label: "Username",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "password",
        label: "Password",
        required: true,
        secret: true,
        type: "password",
      },
    ],
    filing: null,
    guestNotice: {
      recipient: "eVisitor (Hrvatska turistička zajednica)",
      statute: "Zakon o turističkoj pristojbi",
    },
    requiredGuestFields: [],
    serviceKeyField: null,
    shortStayDeadline: null,
    supportsWithdraw: false,
    system: "evisitor",
    timeZone: "Europe/Zagreb",
  },
  istat: {
    alsoFiles: [],
    category: "statistics",
    country: "Italy",
    countryCode: "IT",
    displayName: "ISTAT regional statistics",
    fields: [
      {
        key: "structureCode",
        label: "Structure code",
        required: true,
        secret: false,
        type: "text",
      },
      {
        key: "username",
        label: "Username",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "password",
        label: "Password",
        required: true,
        secret: true,
        type: "password",
      },
    ],
    filing: null,
    guestNotice: null,
    // Statistics, not a per-guest registration.
    requiredGuestFields: [],
    serviceKeyField: null,
    shortStayDeadline: null,
    supportsWithdraw: false,
    system: "istat",
    timeZone: "Europe/Rome",
  },
  ses_hospedajes: {
    alsoFiles: [],
    category: "guest_registration",
    country: "Spain",
    countryCode: "ES",
    displayName: "SES.HOSPEDAJES",
    enrollmentUrl: "https://hospedajes.ses.mir.es/hospedajes-sede/",
    fields: [
      {
        key: "username",
        label: "Web-service username",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "password",
        label: "Password",
        required: true,
        secret: true,
        type: "password",
      },
      {
        accept: ".p12,.pfx,.pem",
        key: "clientCertificate",
        label: "SSL client certificate",
        required: false,
        secret: true,
        type: "file",
      },
      {
        key: "clientCertificatePassword",
        label: "Certificate passphrase",
        required: false,
        secret: true,
        type: "password",
      },
    ],
    filing: null,
    guestNotice: {
      recipient: "Ministerio del Interior (SES.HOSPEDAJES)",
      regionalRecipients: [
        {
          recipient: "Mossos d'Esquadra",
          regions: [
            "Catalonia",
            "Cataluña",
            "Catalunya",
            "Catalogne",
            "Katalonien",
            "Catalogna",
            "Katalonija",
          ],
        },
        {
          recipient: "Ertzaintza",
          regions: [
            "Basque Country",
            "País Vasco",
            "Euskadi",
            "Pays basque",
            "Baskenland",
            "Paesi Baschi",
            "Baskija",
          ],
        },
      ],
      statute: "Real Decreto 933/2021",
    },
    requiredGuestFields: [],
    serviceKeyField: null,
    shortStayDeadline: null,
    supportsWithdraw: false,
    system: "ses_hospedajes",
    // Mainland Spain; the Canaries run an hour behind, so this errs early.
    timeZone: "Europe/Madrid",
  },
  // SIBA moved to the UCFE, a unit inside the Sistema de Segurança Interna,
  // when the SEF was dissolved (Decreto-Lei 41/2023, Portaria 321/2023). AIMA
  // applies the fines but issues no credentials, and siba.sef.pt is dead —
  // most guidance on the open web still says otherwise.
  siba: {
    alsoFiles: [],
    category: "guest_registration",
    country: "Portugal",
    countryCode: "PT",
    displayName: "SIBA",
    enrollmentUrl: "https://siba.ssi.gov.pt",
    fields: [
      {
        key: "nipc",
        label: "Tax number (NIPC)",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "establishment",
        label: "Establishment number",
        required: true,
        secret: true,
        type: "text",
      },
      {
        key: "accessKey",
        label: "Access key (Chave de Acesso)",
        required: true,
        secret: true,
        type: "password",
      },
    ],
    filing: null,
    // Lei 23/2007 reports foreign guests only, so "the property reports its
    // guests to SIBA" would be false for a Portuguese guest. The generic
    // notice ("any guest registration the local law requires") is true for
    // both until the notice can say who a regime covers.
    guestNotice: null,
    requiredGuestFields: [],
    serviceKeyField: null,
    shortStayDeadline: null,
    supportsWithdraw: false,
    system: "siba",
    // Mainland Portugal; the Azores run an hour behind, so this errs early.
    timeZone: "Europe/Lisbon",
  },
};

/**
 * Route a property to its per-guest registration (police) system by
 * ISO-3166-1 alpha-2 country. Only `guest_registration` systems are mapped, so
 * a country's statistics system (Italy's `istat`) never shadows its police one
 * — the map stays 1:1 per country by construction.
 */
export const COUNTRY_TO_REGISTRATION_SYSTEM: Record<
  string,
  RegistrationSystem
> = Object.fromEntries(
  registrationSystemValues
    .filter(
      (system) => REGISTRATION_SYSTEMS[system].category === "guest_registration"
    )
    .map((system) => [REGISTRATION_SYSTEMS[system].countryCode, system])
);

/**
 * The per-guest registration (police) system for a property's country, or null
 * when the country is unset or has no automated system in scope yet. Guest
 * filing routes on this; it never returns a statistics system.
 */
export function registrationSystemForCountry(
  countryCode: string | null | undefined
): RegistrationSystem | null {
  if (!countryCode) {
    return null;
  }
  return COUNTRY_TO_REGISTRATION_SYSTEM[countryCode.toUpperCase()] ?? null;
}

/**
 * The guest registration a property has switched on: its country's system,
 * once the host has picked a municipality in that country and connected that
 * system's account. Null means the property registers no guests, so nothing
 * asks for their IDs.
 */
export function connectedRegistrationSystem(
  place: {
    countryCode: string | null | undefined;
    municipalityCountry: string | null | undefined;
  },
  activeSystems: ReadonlySet<string>
): RegistrationSystem | null {
  const system = registrationSystemForCountry(place.countryCode);
  const picked = place.municipalityCountry === place.countryCode?.toUpperCase();
  return system && picked && activeSystems.has(system) ? system : null;
}

/** Case- and accent-blind, so "Cataluña" and "CATALUNA" match. */
function comparableRegion(region: string): string {
  return region
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

/**
 * What a guest is told about the registration their document feeds, for a
 * property's country and region: null when the country has no per-guest
 * regime modelled, so the portal falls back to the generic wording rather than
 * naming a law that does not apply. A region with its own register names that
 * register instead of the national one.
 */
export function guestRegistrationNoticeForProperty(property: {
  countryCode: string | null | undefined;
  region: string | null | undefined;
}): GuestRegistrationNotice | null {
  const system = registrationSystemForCountry(property.countryCode);
  const spec = system ? REGISTRATION_SYSTEMS[system].guestNotice : null;
  if (!spec) {
    return null;
  }
  const region = property.region ? comparableRegion(property.region) : null;
  const regional = region
    ? spec.regionalRecipients?.find(({ regions }) =>
        regions.some((name) => comparableRegion(name) === region)
      )
    : undefined;
  return {
    recipient: regional?.recipient ?? spec.recipient,
    statute: spec.statute,
  };
}

/**
 * Every registration system serving a property's country — both the per-guest
 * police system and any statistics system (Italy has both). Catalog order. The
 * settings surface renders one credential card per entry; guest routing still
 * uses the single {@link registrationSystemForCountry}.
 */
export function registrationSystemsForCountry(
  countryCode: string | null | undefined
): RegistrationSystem[] {
  if (!countryCode) {
    return [];
  }
  const upper = countryCode.toUpperCase();
  return registrationSystemValues.filter(
    (system) => REGISTRATION_SYSTEMS[system].countryCode === upper
  );
}

/** The picked municipality a property files under (ADR-0142). */
export interface FilingMunicipality {
  /** The enclosing area, e.g. the Italian province `FI`. */
  areaCode: string | null;
  country: string;
}

/**
 * What filing with a system takes from the property's municipality: null
 * where the system is not offered there, else the statistics destination
 * serving its area when the system files to one.
 */
export function systemConfigAt(
  system: RegistrationSystem,
  municipality: FilingMunicipality | null
): { region?: StatisticsRegion } | null {
  if (municipality?.country !== REGISTRATION_SYSTEMS[system].countryCode) {
    return null;
  }
  const destinations = statisticsRegionValues.filter(
    (code) => STATISTICS_REGIONS[code].credentialSystem === system
  );
  if (destinations.length === 0) {
    return {};
  }
  const { areaCode } = municipality;
  const region = destinations.find(
    (code) =>
      areaCode !== null && STATISTICS_REGIONS[code].areaCodes.includes(areaCode)
  );
  return region ? { region } : null;
}

/** The systems offered where a property sits: none until a municipality is picked. */
export function registrationSystemsAt(
  municipality: FilingMunicipality | null
): RegistrationSystem[] {
  return registrationSystemValues.filter(
    (system) => systemConfigAt(system, municipality) !== null
  );
}

/** A setup field that names a facility in a register. */
export type FacilityNumberField = RegistrationFieldDescriptor & {
  facilityRegister: RegistrationNumberScheme;
};

/** The setup field naming the facility `system` files for, where one does. */
export function facilityNumberField(
  system: RegistrationSystem
): FacilityNumberField | null {
  return (
    REGISTRATION_SYSTEMS[system].fields.find(
      (field): field is FacilityNumberField =>
        field.facilityRegister !== undefined
    ) ?? null
  );
}

const HOUR_MS = 60 * 60 * 1000;

/**
 * The obligation a system's statute sets, for the filing engine: null for a
 * system the catalog holds no filing rules for.
 */
export function catalogFilingObligation(
  system: RegistrationSystem
): RegistrationObligation | null {
  const { filing } = REGISTRATION_SYSTEMS[system];
  if (!filing) {
    return null;
  }
  const { arrivalWithinHours, departureWithinHours, whoMustReport } = filing;
  return {
    bucket: "a",
    deadlineHours: arrivalWithinHours.value,
    departureDeadlineHours: departureWithinHours.value ?? undefined,
    departureFilingRequired: departureWithinHours.value !== null,
    system,
    whoMustReport: whoMustReport.value,
  };
}

function isUsableTimeZone(timeZone: string): boolean {
  try {
    return Boolean(
      new Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions().timeZone
    );
  } catch {
    return false;
  }
}

/**
 * The zones a filing deadline is worked out in: the property's own when the
 * runtime can read it, and its filing system's country zone. An unknown system
 * has only the property's zone, read as UTC when unset.
 */
function filingZones(
  system: string,
  propertyTimeZone: string | null
): (string | null)[] {
  const zones: (string | null)[] = [];
  if (propertyTimeZone && isUsableTimeZone(propertyTimeZone)) {
    zones.push(propertyTimeZone);
  }
  const known = RegistrationSystemSchema.safeParse(system);
  if (known.success) {
    zones.push(REGISTRATION_SYSTEMS[known.data].timeZone);
  }
  return zones.length > 0 ? zones : [propertyTimeZone];
}

function soonest(deadlines: Date[]): Date {
  return new Date(Math.min(...deadlines.map((deadline) => deadline.getTime())));
}

/**
 * When a guest's arrival report is legally due. The stay days are the
 * midnight-UTC `check_in_at`/`check_out_at` markers and the times their
 * promised local hours. A guest checked in on the arrival day before the
 * promised hour (ADR-0146) counts from that `arrivedAt` too, so a check-in only
 * ever brings the deadline forward; one at any other time leaves it be.
 *
 * The whole deadline (arrival, stay length, window) is worked out once per
 * {@link filingZones} zone, and the soonest one wins. So a property saved with
 * no zone, an unreadable one, or a real zone that disagrees with its country
 * can only ever make the deadline earlier than the country's own answer: not
 * by reading the hour late ("UTC" for a Rome flat), and not by stretching a
 * short stay past 24 hours across a clock change the country does not share.
 */
export function checkInFilingDeadline(stay: {
  arrivedAt: Date | null;
  checkInDay: Date;
  checkInTime: string;
  checkOutDay: Date | null;
  checkOutTime: string;
  deadlineHours: number;
  propertyTimeZone: string | null;
  system: string;
}): Date {
  const {
    arrivedAt,
    checkInDay,
    checkInTime,
    checkOutDay,
    checkOutTime,
    deadlineHours,
    propertyTimeZone,
    system,
  } = stay;
  const deadlines = filingZones(system, propertyTimeZone).flatMap((zone) => {
    const promisedAt = withLocalTimeOnStayDate(checkInDay, zone, checkInTime);
    const departureAt =
      checkOutDay && withLocalTimeOnStayDate(checkOutDay, zone, checkOutTime);
    // Counted from an early arrival alone, a short stay can stop being short,
    // so both moments are tried and the soonest wins.
    const arrivedEarly =
      arrivedAt &&
      arrivedAt < promisedAt &&
      arrivedAt >= withLocalTimeOnStayDate(checkInDay, zone, "00:00");
    const arrivals = arrivedEarly ? [promisedAt, arrivedAt] : [promisedAt];
    return arrivals.map((arrivalAt) => {
      const hours = arrivalDeadlineHours({
        arrivalAt,
        deadlineHours,
        departureAt,
        system,
      });
      return new Date(arrivalAt.getTime() + hours * HOUR_MS);
    });
  });
  return soonest(deadlines);
}

/**
 * When a stay's departure report is legally due: its promised check-out hour
 * on the departure day plus the window, worked out in each
 * {@link filingZones} zone, the soonest winning.
 */
export function checkOutFilingDeadline(stay: {
  checkOutDay: Date;
  checkOutTime: string;
  deadlineHours: number;
  propertyTimeZone: string | null;
  system: string;
}): Date {
  const { checkOutDay, checkOutTime, deadlineHours, propertyTimeZone, system } =
    stay;
  const deadlines = filingZones(system, propertyTimeZone).map((zone) => {
    const departureAt = withLocalTimeOnStayDate(
      checkOutDay,
      zone,
      checkOutTime
    );
    return new Date(departureAt.getTime() + deadlineHours * HOUR_MS);
  });
  return soonest(deadlines);
}

/**
 * Hours after arrival that one stay's arrival report is legally due: the
 * general deadline, or the system's short-stay deadline when the departure is
 * known and falls within its window, whichever is sooner.
 *
 * `arrivalAt` and `departureAt` are the stay's promised moments: its check-in
 * and check-out hours placed on its stay days in the property's zone. Never
 * pass the `check_in_at`/`check_out_at` day markers themselves: they are
 * midnight UTC, on which every one-night stay measures exactly 24 hours. An
 * unknown arrival or departure takes the general deadline, and the filing is
 * re-anchored once the booking carries both.
 */
export function arrivalDeadlineHours(stay: {
  arrivalAt: Date | null;
  deadlineHours: number;
  departureAt: Date | null;
  system: string;
}): number {
  const { arrivalAt, deadlineHours, departureAt, system } = stay;
  const known = RegistrationSystemSchema.safeParse(system);
  const shortStay = known.success
    ? REGISTRATION_SYSTEMS[known.data].shortStayDeadline
    : null;
  if (!(shortStay && arrivalAt && departureAt)) {
    return deadlineHours;
  }
  const stayMs = departureAt.getTime() - arrivalAt.getTime();
  const isShortStay = stayMs <= shortStay.maxStayHours * HOUR_MS;
  return isShortStay
    ? Math.min(deadlineHours, shortStay.deadlineHours)
    : deadlineHours;
}

/**
 * The guests of a batch arriving together that `system` cannot file without
 * an ID document they lack: every one without, or none once one carries one
 * where only the party's head must.
 */
export function guestsOwingDocument<Guest>(
  system: RegistrationSystem | null,
  batch: readonly Guest[],
  hasDocument: (guest: Guest) => boolean
): Guest[] {
  const requirement =
    system &&
    REGISTRATION_SYSTEMS[system].requiredGuestFields.find(
      ({ field }) => field === "documentNumber"
    );
  if (!requirement) {
    return [];
  }
  const without = batch.filter((guest) => !hasDocument(guest));
  return requirement.headOnly && without.length < batch.length ? [] : without;
}

/**
 * Everything a property's registration regime asks of each guest: the floor
 * its filing system cannot file without. The one list every capture surface
 * asks for (the guest portal and the staff scanner, which also corrects). The
 * country resolves the system.
 */
export function requiredRegistrationFields(
  countryCode: string | null | undefined
): readonly RegistrationFieldRequirement[] {
  const system = registrationSystemForCountry(countryCode);
  return system ? REGISTRATION_SYSTEMS[system].requiredGuestFields : [];
}
